import React, { createContext, useContext, useState, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { MelaButton } from "@/ui";
import { MELA_PHONE_ACTIONS_ROW, MELA_PHONE_QUERY } from "@/ui/mela";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import type { DateView } from "@mui/x-date-pickers/models";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import {
  startOfMonth,
  subMonths,
  addMonths,
  isSameDay,
  isAfter,
  isBefore,
  isWithinInterval,
  differenceInDays,
  format,
  startOfDay,
  endOfDay,
} from "date-fns";
import colors from "../../../constants/colors";
import { useTranslation } from "react-i18next";
import { getLocalizationAdapterLocale } from "@/utils/utils";
import { SHORTCUT_CONFIGS, ShortcutKey } from "./DateRangePickerShortcuts";

interface DateRangePickerContentProps {
  initialRange: [Date | null, Date | null];
  onApply: (range: [Date | null, Date | null]) => void;
  onCancel: () => void;
  minDate?: Date;
  maxDate?: Date;
  maxRangeDays?: number;
  shortcuts?: ShortcutKey[];
}

// Backend range filters are inclusive (gte/lte) on full timestamps, so "to"
// must cover the whole last day or its documents are silently excluded.
const normalizeRange = ([from, to]: [Date | null, Date | null]): [
  Date | null,
  Date | null,
] => [from ? startOfDay(from) : from, to ? endOfDay(to) : to];

const DEFAULT_SHORTCUTS: ShortcutKey[] = [
  "today",
  "yesterday",
  "last7",
  "thisMonth",
  "lastMonth",
  "lastQuarter",
  "last6Months",
  "last12Months",
  "lastYear",
  "thisYear",
];

const isShortcutAvailable = (key: ShortcutKey, maxRangeDays?: number) => {
  const config = SHORTCUT_CONFIGS[key];
  if (!config) return false;
  if (!maxRangeDays) return true;

  const [s, e] = config.getRange();
  return differenceInDays(e, s) + 1 <= maxRangeDays;
};

const CALENDAR_SX = {
  "& .MuiDateCalendar-root": {
    height: "auto",
    maxHeight: 350,
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    color: "white !important",
    backgroundColor: `${colors.blue || "#00b0f0"} !important`,
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    color: "white !important",
    backgroundColor: `${colors.blue || "#00b0f0"} !important`,
  },
};

const PHONE_CALENDAR_SX = {
  ...CALENDAR_SX,
  "& .MuiDateCalendar-root, & .MuiMonthCalendar-root, & .MuiYearCalendar-root":
    { width: "100%" },
};

const isInRange = (
  date: Date,
  [start, end]: [Date | null, Date | null],
  hoverDate: Date | null,
) => {
  if (start && end) {
    return (
      isWithinInterval(date, { start, end }) ||
      isSameDay(date, start) ||
      isSameDay(date, end)
    );
  }
  if (start && hoverDate) {
    const actualStart = isBefore(start, hoverDate) ? start : hoverDate;
    const actualEnd = isBefore(start, hoverDate) ? hoverDate : start;
    return (
      isWithinInterval(date, { start: actualStart, end: actualEnd }) ||
      isSameDay(date, actualStart) ||
      isSameDay(date, actualEnd)
    );
  }
  return false;
};

interface RangeDayState {
  range: [Date | null, Date | null];
  hoverDate: Date | null;
  setHoverDate: (date: Date) => void;
}

const RangeDayContext = createContext<RangeDayState>({
  range: [null, null],
  hoverDate: null,
  setHoverDate: () => {},
});

// Outside the picker so hover updates re-render the day cells instead of
// remounting them: a remount can swallow a tap.
const RangeDay = (props: PickersDayProps) => {
  const { range, hoverDate, setHoverDate } = useContext(RangeDayContext);
  const { day, ...other } = props;
  const isStart = !!range[0] && isSameDay(range[0], day);
  const isEnd = !!range[1] && isSameDay(range[1], day);
  const selected = isStart || isEnd;
  const inRange = isInRange(day, range, hoverDate);

  return (
    <Box
      onMouseEnter={() => !range[1] && setHoverDate(day)}
      sx={{
        position: "relative",
        ...(inRange &&
          !selected && {
            backgroundColor: `${colors.lightBlue || "#e3f2fd"}`,
            borderRadius: 0,
          }),
        ...(isStart &&
          range[1] && {
            borderTopLeftRadius: "50%",
            borderBottomLeftRadius: "50%",
            backgroundColor: colors.lightBlue || "#e3f2fd",
          }),
        ...(isEnd && {
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
          backgroundColor: colors.lightBlue || "#e3f2fd",
        }),
      }}
    >
      <PickersDay
        {...other}
        day={day}
        selected={selected}
        sx={{
          ...(selected && {
            backgroundColor: `${colors.blue || "#00b0f0"} !important`,
            color: "white !important",
          }),
        }}
      />
    </Box>
  );
};

const RANGE_DAY_SLOTS = { day: RangeDay };

const DateRangePickerContent = ({
  initialRange,
  onApply,
  onCancel,
  minDate,
  maxDate,
  maxRangeDays,
  shortcuts = DEFAULT_SHORTCUTS,
}: DateRangePickerContentProps) => {
  const { t, i18n } = useTranslation();
  const isPhone = useMediaQuery(MELA_PHONE_QUERY);
  const [range, setRange] = useState<[Date | null, Date | null]>(initialRange);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const dateLocale = useMemo(
    () => getLocalizationAdapterLocale(i18n.language),
    [i18n.language],
  );

  const rangeDayState = useMemo<RangeDayState>(
    () => ({ range, hoverDate, setHoverDate }),
    [range, hoverDate],
  );

  const rangeInfo = useMemo(() => {
    const [start, end] = range;
    if (!start) return { totalDays: 0, isInvalid: true, isComplete: false };

    const days = differenceInDays(end || hoverDate || start, start) + 1;
    const isComplete = !!(start && end);
    const isOverLimit = !!(maxRangeDays && days > maxRangeDays);

    return {
      totalDays: days,
      isInvalid: !isComplete || isOverLimit,
      isError: isOverLimit,
      isComplete,
    };
  }, [range, hoverDate, maxRangeDays]);

  // Left calendar month (the right one will be next month)
  const [leftMonth, setLeftMonth] = useState<Date>(
    range[0] ? startOfMonth(range[0]) : startOfMonth(new Date()),
  );

  const rightMonth = useMemo(() => addMonths(leftMonth, 1), [leftMonth]);

  const handleStartChange = (date: Date | null) => {
    if (!date) return;
    const [_, end] = range;
    if (end && isAfter(date, end)) {
      setRange([date, null]);
    } else {
      setRange([date, end]);
    }
  };

  const handleEndChange = (date: Date | null) => {
    if (!date) return;
    const [start, _] = range;
    if (start && isBefore(date, start)) {
      setRange([null, date]);
    } else {
      setRange([start, date]);
    }
  };

  // Phone: the first tap starts the range, the second closes it; year and month picks only navigate.
  const handlePhoneDayChange = (
    date: Date | null,
    _selectionState?: unknown,
    selectedView?: DateView,
  ) => {
    if (!date || selectedView !== "day") return;
    const [start, end] = range;
    if (!start || end || isBefore(date, start)) {
      setRange([date, null]);
      setHoverDate(null);
      return;
    }
    setRange([start, date]);
  };

  const handleShortcutClick = (type: ShortcutKey) => {
    const config = SHORTCUT_CONFIGS[type];
    if (!config) return;

    const newRange = config.getRange();
    setRange(newRange);
    setLeftMonth(startOfMonth(newRange[0]));
    onApply(newRange);
  };

  const availableShortcuts = shortcuts.filter((key) =>
    isShortcutAvailable(key, maxRangeDays),
  );

  const getSummary = () => {
    const [start, end] = range;
    if (!start)
      return (
        <div className="text-gray-500">
          {t("ui.dataRange.placeholder.selectRange")}
        </div>
      );

    const startStr = format(start, "dd MMM yyyy", { locale: dateLocale });
    const endStr = end
      ? format(end, "dd MMM yyyy", { locale: dateLocale })
      : "...";
    const { totalDays, isError } = rangeInfo;

    const daysLabel =
      totalDays === 1
        ? t("ui.dataRange.summary.day")
        : t("ui.dataRange.summary.days");

    return (
      <div className={isError ? "text-red-500 font-bold" : "text-gray-700"}>
        {startStr} - {endStr} ({totalDays} {daysLabel})
      </div>
    );
  };

  const applyButton = (
    <MelaButton
      variant="solid"
      tone="primary"
      onClick={() => onApply(normalizeRange(range))}
      disabled={rangeInfo.isInvalid}
    >
      {t("ui.dataRange.actions.apply")}
    </MelaButton>
  );

  if (isPhone) {
    return (
      <RangeDayContext.Provider value={rangeDayState}>
        {/* The width cap lives here so every popover keeps the picker on screen. */}
        <div className="flex flex-col w-[calc(100vw-32px)] max-w-full max-h-[calc(100dvh-32px)] bg-white rounded-lg overflow-hidden shadow-lg">
          <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-gray p-2 [--mela-button-min-width:max-content]">
            {availableShortcuts.map((key) => (
              <MelaButton
                key={key}
                size="sm"
                variant="outline"
                tone="neutral"
                onClick={() => handleShortcutClick(key)}
              >
                {t(SHORTCUT_CONFIGS[key].label)}
              </MelaButton>
            ))}
          </div>

          <div className="min-h-0 overflow-y-auto pt-2">
            <Box sx={PHONE_CALENDAR_SX}>
              <DateCalendar
                value={range[0] || leftMonth}
                onChange={handlePhoneDayChange}
                onMonthChange={(m) => setLeftMonth(m)}
                slots={RANGE_DAY_SLOTS}
                minDate={minDate}
                maxDate={maxDate}
                views={["year", "month", "day"]}
              />
            </Box>
          </div>

          <div className="flex shrink-0 flex-col gap-3 p-4 pt-2">
            {getSummary()}
            <Box sx={{ display: "flex", gap: 1, ...MELA_PHONE_ACTIONS_ROW }}>
              <MelaButton variant="soft" tone="neutral" onClick={onCancel}>
                {t("button.cancel.translation")}
              </MelaButton>
              {applyButton}
            </Box>
          </div>
        </div>
      </RangeDayContext.Provider>
    );
  }

  return (
    <RangeDayContext.Provider value={rangeDayState}>
      <div className="flex bg-white rounded-lg overflow-hidden shadow-lg">
        {/* Sidebar Shortcuts */}
        <div className="w-50 border-r border-gray p-2 flex flex-col bg-white">
          <List>
            {availableShortcuts.map((key) => (
              <ListItem key={key} disablePadding>
                <ListItemButton
                  onClick={() => handleShortcutClick(key)}
                  sx={{ borderRadius: 1 }}
                  dense
                >
                  <ListItemText
                    primary={t(SHORTCUT_CONFIGS[key].label)}
                    slotProps={{
                      primary: { variant: "body2" },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </div>

        {/* Main Content: Calendars + Footer */}
        <div className="flex flex-col">
          {/* Calendars */}
          <div className="flex px-4 pt-2 gap-1">
            <Box sx={CALENDAR_SX}>
              <DateCalendar
                value={range[0] || leftMonth}
                onChange={handleStartChange}
                onMonthChange={(m) => setLeftMonth(m)}
                slots={RANGE_DAY_SLOTS}
                minDate={minDate}
                maxDate={maxDate}
                views={["year", "month", "day"]}
              />
            </Box>
            <Divider orientation="vertical" flexItem className="my-4" />
            <Box sx={CALENDAR_SX}>
              <DateCalendar
                value={range[1] || rightMonth}
                onChange={handleEndChange}
                onMonthChange={(m) => setLeftMonth(subMonths(m, 1))}
                slots={RANGE_DAY_SLOTS}
                minDate={minDate}
                maxDate={maxDate}
                views={["year", "month", "day"]}
              />
            </Box>
          </div>

          {/* Footer actions aligned with calendars */}
          <div className="p-4 pt-0 flex items-center justify-end gap-4 bg-white">
            <div className="">{getSummary()}</div>
            {applyButton}
          </div>
        </div>
      </div>
    </RangeDayContext.Provider>
  );
};

export default DateRangePickerContent;
